// eslint-disable-next-line eag/no-external-date-time-format-functions -- Custom format
import {format} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';

import {ReactNode} from 'react';
import {useSelector} from 'react-redux';

import {isEmpty, isNil, isNotEmpty} from 'ramda';

import {InspectionState, InspectionStateLiteral} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import {usePermissions} from '@dms/shared';

import {useThunkDispatch} from '../../../hooks/useThunkDispatch';
import {getActualAuditData, updateAudit} from '../../../store/carAudit/actions';
import {setErrorCategories, updateAuditFields} from '../../../store/carAudit/reducer';
import {
  AuditData,
  AuditFields,
  getAuditDataFromAutoFillTemplate,
  getInitialAuditData,
} from '../../../store/carAudit/reducerUtils';
import {
  selectAudit,
  selectAuditState,
  selectCategoriesIdsToOverrideByTemplate,
  selectNavigationCategories,
  selectNavigationCategoriesStateParams,
} from '../../../store/carAudit/selectors';
import {AuditCategoriesWithError, AuditValidationError} from '../../../types/ConditionTypes';
import {LoadAuditDataResponseItemBody} from '../../../types/LoadAuditDataResponseItemBody';
import {RequestAuditDataFieldsBody} from '../../../types/RequestAuditDataFieldsBody';
import {ConditionContext, TConditionContext} from '../hooks/useConditionContext';
import {useSecondSetValidation} from '../hooks/useSecondSetValidation';
import {useVehicleFeatures} from '../hooks/useVehicleFeatures';
import {AuditCategoryUniqueKey} from '../types/AuditCategoryUniqueKey';
import {NavigationState} from '../types/NavigationState';
import {getCategoriesWithError} from '../utils/getCategoriesWithError';
import {getFormFieldName} from '../utils/getFormFieldName';
import {getInitialFormValues} from '../utils/getInitialFormValues';

type AuditContextProps = Omit<
  TConditionContext,
  | 'loadActualData'
  | 'loadActualDataAndValidate'
  | 'handleChangeCategory'
  | 'hasPermissionsToEditInspectionData'
  | 'isDisabledAuditEdit'
  | 'isDisabledForUser'
  | 'isUploadInProgress'
  | 'setIsUploadInProgress'
> & {
  setIsAutoSaveDisabled: (isAutoSaveDisabled: boolean) => void;
  getChangedCategoryStateFieldValue: () => AuditFields;
  updateFields: (formFields: AuditFields) => Promise<void>;
  children?: ReactNode;
};

export const AuditContext = ({
  formRenderProps,
  requiredFormFields,
  isCreateVehicle,
  setIsAutoSaveDisabled,
  getChangedCategoryStateFieldValue,
  updateFields,
  children,
  isInNotEditableState,
}: AuditContextProps) => {
  const dispatch = useThunkDispatch();
  const navigationStateParams = useSelector(selectNavigationCategoriesStateParams);
  const navigationCategories = useSelector(selectNavigationCategories);
  const audit = useSelector(selectAudit);
  const auditState = useSelector(selectAuditState);
  const {
    featureCategoryExists,
    selectedFeaturesCategoryIds,
    selectedFeaturesAssetCategoryIds,
    featuresCategoriesId,
    featuresMainCategoriesIds,
    featuresAssetsCategoriesIds,
  } = useVehicleFeatures(
    navigationCategories?.find(
      (category) => category.uniqueKey === AuditCategoryUniqueKey.EQUIPMENT
    )
  );
  const {filterSecondSetValidateError} = useSecondSetValidation(
    navigationCategories?.find(
      (category) => category.uniqueKey === AuditCategoryUniqueKey.WHEELS_AND_TIRES
    )
  );

  const [permissionToCreateInspection, permissionToUpdateInspection] = usePermissions({
    permissionKeys: ['createInspection', 'updateInspection'],
    scopes: {
      createInspection: {
        inspectionType: audit?.inspectionType,
      },
      updateInspection: {
        inspectionType: audit?.inspectionType,
        inspectionState: audit?.state,
        // Gated by inspection type/state, not participation
        participation: 'SKIP',
      },
    },
  });

  const categoriesIdsToOverride = useSelector(selectCategoriesIdsToOverrideByTemplate);
  const {
    handleSubmit,
    form: {initialize, pauseValidation, resumeValidation, setConfig},
  } = formRenderProps;

  const setNavigationCategoryStates = async (
    categoriesWithError: AuditCategoriesWithError,
    auditState: InspectionStateLiteral
  ): Promise<void> => {
    const isAuditFinished =
      auditState && auditState === LoadAuditDataResponseItemBody.state.FINISHED_AUDIT;

    if (!navigationCategories || !audit || isAuditFinished) {
      return;
    }

    const finishedState = `${NavigationState.finished}-${format(new Date(), 'dd/MM/yy')}`;
    const requestBody: RequestAuditDataFieldsBody[] = [];
    const stateValues: AuditFields = {};

    for await (const {id: categoryId} of navigationCategories) {
      const paramDefinitionId = navigationStateParams?.[categoryId];
      if (!paramDefinitionId) {
        continue;
      }

      const value = categoriesWithError?.[categoryId]
        ? NavigationState.notFinishedError
        : finishedState;

      requestBody.push({
        categoryId,
        paramDefinitionId,
        value,
        updatedAt: new Date().toISOString(),
      });

      stateValues[getFormFieldName(categoryId, paramDefinitionId)] = value;
    }
    await dispatch(
      updateAudit.action({
        auditId: audit.id,
        requestBody,
      })
    );
    await dispatch(updateAuditFields(stateValues));
  };

  const filterFeatureCategoryErrors = (categoriesWithError: AuditCategoriesWithError) =>
    Object.keys(categoriesWithError)
      .filter((categoryWithError) => {
        if (featuresCategoriesId?.includes(categoryWithError)) {
          return selectedFeaturesCategoryIds?.includes(categoryWithError);
        }
        if (featuresAssetsCategoriesIds?.includes(categoryWithError)) {
          return selectedFeaturesAssetCategoryIds?.includes(categoryWithError);
        }
        return true;
      })
      .filter((categoriesWithError, _, previousErrors) => {
        if (
          !previousErrors.some((previousError) =>
            selectedFeaturesCategoryIds?.includes(previousError)
          )
        ) {
          if (featuresMainCategoriesIds.includes(categoriesWithError)) {
            return false;
          }
        }

        return true;
      })
      .reduce((obj: AuditCategoriesWithError, key) => {
        obj[key] = categoriesWithError[key];
        return obj;
      }, {});

  const getFilteredCategoriesWithError = (audit: AuditData): AuditCategoriesWithError => {
    const categoriesWithError = getCategoriesWithError(audit, requiredFormFields);

    filterSecondSetValidateError(categoriesWithError);

    return featureCategoryExists
      ? filterFeatureCategoryErrors(categoriesWithError)
      : categoriesWithError;
  };

  const _loadActualData = async (auditId: string): Promise<AuditData | null> => {
    const result = await dispatch(
      getActualAuditData.action({
        auditId,
      })
    );

    if (getActualAuditData.action.rejected.match(result)) {
      return null;
    }

    const actualAuditData = getInitialAuditData(result.payload[0]);

    if (audit && result.payload) {
      const autoFillTemplate = result.payload[0];

      const auditWithAutoFillTemplate = autoFillTemplate
        ? getAuditDataFromAutoFillTemplate(audit, autoFillTemplate, categoriesIdsToOverride)
        : null;

      const updatedAuditFields = {...audit.fields, ...auditWithAutoFillTemplate};

      setConfig('keepDirtyOnReinitialize', false);
      initialize(getInitialFormValues(updatedAuditFields, navigationStateParams) ?? {});
      setConfig('keepDirtyOnReinitialize', true);
    }

    return actualAuditData;
  };

  const loadActualData = async (auditId: string): Promise<AuditData | null> => {
    setIsAutoSaveDisabled(true);
    const audit = await _loadActualData(auditId);
    setIsAutoSaveDisabled(false);

    return audit;
  };

  const validate = async (audit: AuditData): Promise<void> => {
    const categoriesWithError = getFilteredCategoriesWithError(audit);

    await dispatch(setErrorCategories(categoriesWithError));
    await handleSubmit();
    await setNavigationCategoryStates(categoriesWithError, audit.state);

    if (isNotEmpty(categoriesWithError)) {
      const validationError: AuditValidationError = {categoriesWithError};

      return Promise.reject(validationError);
    }
  };

  const loadActualDataAndValidate = async (): Promise<void> => {
    if (!audit) {
      return;
    }
    if (
      auditState === 'to_revision' &&
      isFeatureEnabled(featureFlags.INSPECTION_TO_REVISION_NO_EDIT)
    ) {
      return;
    }
    try {
      setIsAutoSaveDisabled(true);
      const actualData = await _loadActualData(audit.id);

      // Validating a stale snapshot could pass an inspection the server already contradicts
      if (isNil(actualData)) {
        throw new Error('Actual audit data could not be loaded');
      }

      resumeValidation();
      await validate(actualData);
    } finally {
      //@ts-expect-error pauseValidation has parameter preventNotification
      pauseValidation(false);
      setIsAutoSaveDisabled(false);
    }
  };

  const handleChangeCategory = async (): Promise<void> => {
    const stateFieldValue = getChangedCategoryStateFieldValue();
    if (isEmpty(stateFieldValue)) {
      return;
    }

    await updateFields(stateFieldValue);
  };

  const isDisabledAuditEdit = (includeState = true): boolean => {
    if (isInNotEditableState && includeState) {
      return true;
    }
    return !permissionToUpdateInspection;
  };

  const hasPermissionsToEditInspectionData = (): boolean => {
    const hasPermissionsToEditInspection =
      permissionToUpdateInspection ||
      (permissionToCreateInspection &&
        auditState !== LoadAuditDataResponseItemBody.state.FINISHED_AUDIT);

    const disableInspectionEditInToRevisionState =
      auditState &&
      (
        [InspectionState.TO_REVISION, InspectionState.FINISHED_AUDIT] as (typeof auditState)[]
      ).includes(auditState);

    return (hasPermissionsToEditInspection || false) && !disableInspectionEditInToRevisionState;
  };

  return (
    <ConditionContext.Provider
      value={{
        formRenderProps,
        requiredFormFields,
        isCreateVehicle,
        loadActualData,
        loadActualDataAndValidate,
        handleChangeCategory,
        hasPermissionsToEditInspectionData,
        isDisabledAuditEdit,
        isInNotEditableState,
        isDisabledForUser: isDisabledAuditEdit() || !hasPermissionsToEditInspectionData(),
      }}
    >
      {children}
    </ConditionContext.Provider>
  );
};
